import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, ShieldCheck, Target, Calendar } from 'lucide-react';
import { supabase, Transaction, Category } from '../lib/supabase';
import { getCurrentSchoolYear, getSchoolYearLabel, isInSchoolYear, getSchoolMonths } from '../utils/schoolYear';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useFiscalYear } from '../hooks/useFiscalYear';
import { KpiCard } from './KpiCard';
import { FeatureTeaser } from './FeatureTeaser';
import { UpgradeModal } from './UpgradeModal';
import { ReferralCard } from './ReferralCard';

interface DashboardTabProps {
  onAddTransaction?: () => void;
  onCreateBudget?: () => void;
  onViewCashFlow?: () => void;
  onUpgrade?: () => void;
}

export function DashboardTab({
  onAddTransaction,
  onCreateBudget,
  onViewCashFlow,
  onUpgrade
}: DashboardTabProps) {
  const { effectiveUserId } = useAuth();
  const { planTier, canAccessBudget, canAccessStressTests } = useSubscriptionAccess();
  const { startMonth, startDay, loading: fiscalYearLoading } = useFiscalYear();
  const [transactions, setTransactions] = useState<(Transaction & { category?: Category })[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadTransactions();
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [effectiveUserId]);

  function updateTime() {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    setLastUpdateTime(`${hours}:${minutes}`);
  }

  async function loadTransactions() {
    if (!effectiveUserId) return;

    const { data, error } = await supabase
      .from('transactions')
      .select('*, categories(*)')
      .eq('user_id', effectiveUserId)
      .order('date', { ascending: true });

    if (!error && data) {
      setTransactions(data.map(t => ({ ...t, category: t.categories })));
    }
  }

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentSchoolYear = getCurrentSchoolYear(startMonth, startDay);

  const monthTransactions = transactions.filter(t => {
    const date = new Date(t.date);
    return !t.is_internal_transfer && date.getMonth() + 1 === currentMonth && isInSchoolYear(date, currentSchoolYear, startMonth, startDay);
  });

  const yearTransactions = transactions.filter(t => {
    const date = new Date(t.date);
    return !t.is_internal_transfer && isInSchoolYear(date, currentSchoolYear, startMonth, startDay);
  });

  const previousMonthTransactions = transactions.filter(t => {
    const date = new Date(t.date);
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    return !t.is_internal_transfer && date.getMonth() + 1 === prevMonth && isInSchoolYear(date, currentSchoolYear, startMonth, startDay);
  });

  const monthIncome = monthTransactions
    .filter(t => Number(t.amount) > 0)
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const monthExpense = Math.abs(
    monthTransactions
      .filter(t => Number(t.amount) < 0)
      .reduce((sum, t) => sum + Number(t.amount), 0)
  );

  const monthResult = monthIncome - monthExpense;

  const yearIncome = yearTransactions
    .filter(t => Number(t.amount) > 0)
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const yearExpense = Math.abs(
    yearTransactions
      .filter(t => Number(t.amount) < 0)
      .reduce((sum, t) => sum + Number(t.amount), 0)
  );

  const yearResult = yearIncome - yearExpense;

  const previousMonthExpense = Math.abs(
    previousMonthTransactions
      .filter(t => Number(t.amount) < 0)
      .reduce((sum, t) => sum + Number(t.amount), 0)
  );

  const avgMonthlyExpense = previousMonthExpense > 0 ? previousMonthExpense : monthExpense;

  function getIncomeInsight(): string {
    if (monthIncome === 0) {
      return "Aucune recette enregistrée ce mois-ci";
    }
    return `${monthTransactions.filter(t => Number(t.amount) > 0).length} recettes ce mois`;
  }

  function getExpenseInsight(): string {
    if (monthExpense === 0) {
      return "Aucune dépense enregistrée ce mois-ci";
    }
    if (avgMonthlyExpense > 0 && monthExpense > avgMonthlyExpense * 1.2) {
      return "Dépenses supérieures à la moyenne du mois dernier";
    }
    if (avgMonthlyExpense > 0 && monthExpense < avgMonthlyExpense * 0.8) {
      return "Dépenses inférieures à la moyenne du mois dernier";
    }
    return `${monthTransactions.filter(t => Number(t.amount) < 0).length} dépenses ce mois`;
  }

  function getResultInsight(): string {
    if (monthResult < -1000) {
      return "Résultat négatif — à surveiller de près";
    }
    if (monthResult < 0) {
      return "Résultat négatif — attention aux dépenses";
    }
    if (monthResult > 1000) {
      return "Excellent résultat ce mois";
    }
    return "Résultat équilibré ce mois";
  }

  function getResultStatus(): 'success' | 'warning' | 'danger' | 'neutral' {
    if (monthResult > 0) return 'success';
    if (monthResult > -1000) return 'warning';
    return 'danger';
  }

  function getYearIncomeInsight(): string {
    if (yearIncome === 0) {
      return "Aucune recette enregistrée sur l'exercice";
    }
    const count = yearTransactions.filter(t => Number(t.amount) > 0).length;
    return `${count} recette${count > 1 ? 's' : ''} sur l'exercice`;
  }

  function getYearExpenseInsight(): string {
    if (yearExpense === 0) {
      return "Aucune dépense enregistrée sur l'exercice";
    }
    const count = yearTransactions.filter(t => Number(t.amount) < 0).length;
    return `${count} dépense${count > 1 ? 's' : ''} sur l'exercice`;
  }

  function getYearResultInsight(): string {
    if (yearResult < -5000) {
      return "Résultat négatif important sur l'exercice";
    }
    if (yearResult < 0) {
      return "Résultat négatif sur l'exercice";
    }
    if (yearResult > 10000) {
      return "Excellent résultat sur l'exercice";
    }
    if (yearResult > 0) {
      return "Résultat positif sur l'exercice";
    }
    return "Résultat équilibré sur l'exercice";
  }

  function getYearResultStatus(): 'success' | 'warning' | 'danger' | 'neutral' {
    if (yearResult > 0) return 'success';
    if (yearResult > -5000) return 'warning';
    return 'danger';
  }

  const schoolMonths = getSchoolMonths(startMonth);

  if (fiscalYearLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Tableau de bord</h2>
            <p className="text-base text-gray-600 mt-1">Situation du jour</p>
            <p className="text-xs text-gray-500 mt-1">Dernière mise à jour : {lastUpdateTime}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onAddTransaction}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              + Ajouter un mouvement
            </button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ce mois</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <KpiCard
            title="Recettes du mois"
            amount={monthIncome}
            icon={TrendingUp}
            insight={getIncomeInsight()}
            status={monthIncome > 0 ? 'success' : 'neutral'}
          />
          <KpiCard
            title="Dépenses du mois"
            amount={-monthExpense}
            icon={TrendingDown}
            insight={getExpenseInsight()}
            status={monthExpense > 0 ? 'danger' : 'neutral'}
          />
          <KpiCard
            title="Résultat du mois"
            amount={monthResult}
            icon={DollarSign}
            insight={getResultInsight()}
            status={getResultStatus()}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          L'exercice {getSchoolYearLabel(currentSchoolYear)}
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <KpiCard
            title="Recettes de l'exercice"
            amount={yearIncome}
            icon={TrendingUp}
            insight={getYearIncomeInsight()}
            status={yearIncome > 0 ? 'success' : 'neutral'}
          />
          <KpiCard
            title="Dépenses de l'exercice"
            amount={-yearExpense}
            icon={TrendingDown}
            insight={getYearExpenseInsight()}
            status={yearExpense > 0 ? 'danger' : 'neutral'}
          />
          <KpiCard
            title="Résultat de l'exercice"
            amount={yearResult}
            icon={DollarSign}
            insight={getYearResultInsight()}
            status={getYearResultStatus()}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ReferralCard />

        {!canAccessBudget && (
          <FeatureTeaser
            title="Prévisionnel"
            description="Projetez votre trésorerie sur plusieurs mois et testez vos décisions avant de les prendre"
            icon={Target}
            buttonText="Activer Anticipation"
            onClick={() => setShowUpgradeModal(true)}
            locked={true}
            planName="Anticipation"
          />
        )}

        {canAccessBudget && !canAccessStressTests && (
          <div className="bg-white rounded-lg p-6 border-2 border-blue-200 hover:shadow-md transition-all">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg">
                <Target size={28} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Prévisionnel</h3>
                <span className="inline-block px-2 py-0.5 bg-green-600 text-white text-xs font-semibold rounded mb-2">
                  Activé
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-4">Créez et gérez vos budgets prévisionnels</p>
            <button
              onClick={onCreateBudget}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Créer un budget
            </button>
          </div>
        )}

        {!canAccessStressTests && (
          <FeatureTeaser
            title="Sécurité"
            description="Stress test : combien de temps tenez-vous sans encaissement ? Analysez votre résilience financière"
            icon={ShieldCheck}
            buttonText={planTier === 'vision' ? 'Activer Sécurité' : 'Passer à Sécurité'}
            onClick={() => setShowUpgradeModal(true)}
            locked={true}
            planName="Sécurité"
          />
        )}

        {canAccessStressTests && (
          <div className="bg-white rounded-lg p-6 border-2 border-green-200 hover:shadow-md transition-all">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 p-3 bg-green-50 rounded-lg">
                <ShieldCheck size={28} className="text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Sécurité</h3>
                <span className="inline-block px-2 py-0.5 bg-green-600 text-white text-xs font-semibold rounded mb-2">
                  Activé
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-4">Tests de résistance et indicateurs avancés disponibles</p>
            <button
              onClick={onViewCashFlow}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Voir les stress tests
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <Calendar className="text-blue-600" size={24} />
          Évolution mensuelle
        </h3>
        <div className="space-y-4">
          {schoolMonths.map(schoolMonth => {
            const monthTrans = yearTransactions.filter(t => {
              const date = new Date(t.date);
              return !t.is_internal_transfer && date.getMonth() + 1 === schoolMonth.calendarMonth;
            });

            const income = monthTrans
              .filter(t => Number(t.amount) > 0)
              .reduce((sum, t) => sum + Number(t.amount), 0);

            const expense = Math.abs(
              monthTrans
                .filter(t => Number(t.amount) < 0)
                .reduce((sum, t) => sum + Number(t.amount), 0)
            );

            const maxValue = Math.max(
              ...schoolMonths.map(m => {
                const mTrans = yearTransactions.filter(t => {
                  const date = new Date(t.date);
                  return date.getMonth() + 1 === m.calendarMonth;
                });
                const inc = mTrans.filter(t => Number(t.amount) > 0).reduce((sum, t) => sum + Number(t.amount), 0);
                const exp = Math.abs(mTrans.filter(t => Number(t.amount) < 0).reduce((sum, t) => sum + Number(t.amount), 0));
                return Math.max(inc, exp);
              }),
              1000
            );

            return (
              <div key={schoolMonth.calendarMonth} className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium text-gray-700 w-24">{schoolMonth.name}</span>
                  <div className="flex-1 mx-4 relative h-8">
                    <div className="absolute inset-0 flex items-center gap-1">
                      <div
                        className="bg-green-500 h-full rounded transition-all hover:bg-green-600"
                        style={{ width: `${(income / maxValue) * 100}%` }}
                        title={`Recettes: ${income.toFixed(2)} €`}
                      />
                      <div
                        className="bg-red-500 h-full rounded transition-all hover:bg-red-600"
                        style={{ width: `${(expense / maxValue) * 100}%` }}
                        title={`Dépenses: ${expense.toFixed(2)} €`}
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs w-48 justify-end">
                    <span className="text-green-600 font-medium">+{income.toFixed(0)} €</span>
                    <span className="text-red-600 font-medium">-{expense.toFixed(0)} €</span>
                  </div>
                </div>
                {schoolMonth.calendarMonth === currentMonth && (
                  <div className="ml-24 text-xs text-blue-600 font-medium">
                    → Mois en cours
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-6 pt-4 border-t border-gray-200 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-gray-600">Recettes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-gray-600">Dépenses</span>
          </div>
        </div>
      </div>

      {showUpgradeModal && (
        <UpgradeModal
          onClose={() => setShowUpgradeModal(false)}
          suggestedPlan={planTier === 'vision' ? 'anticipation' : 'securite'}
        />
      )}
    </div>
  );
}
